import {makeObservable, observable, runInAction} from 'mobx';

import type {
  CoreMediaFilesAddUrlParams,
  CoreMediaFilesPrepareUploadParams,
  CoreMediaFilesReadParams,
  CoreMediaLinksDeleteParams,
} from '../../types/core/methods';
import type {FileApiModel} from './types';

import {getUploadParams} from '../../../utils/entity/file/getUploadParams';
import {apiRequest} from '../../utils/apiRequest';
import {EntityStore} from '../../utils/entityStore';

type UploadInfo<T> = {
  error: any;
  file: null | T;
  inProgress: boolean;
  progress: number;
  success: boolean;
};

export class FileStore<
  T extends FileApiModel = FileApiModel,
> extends EntityStore<
  T,
  CoreMediaFilesReadParams,
  object,
  object,
  CoreMediaLinksDeleteParams
> {
  uploading = new Map<string, UploadInfo<T>>();

  constructor(...args: ConstructorParameters<typeof EntityStore>) {
    super(...args);

    makeObservable(this, {
      uploading: observable,
    });
  }

  async addUrl(url: string) {
    const {file} = await apiRequest<CoreMediaFilesAddUrlParams>({
      method: 'media.files.add_url',
      params: {
        url,
      },
    });

    return file;
  }

  async upload(
    input: File | string,
    {
      authorId,
      mimetype,
      name,
      onProgress,
      uploadingKey,
    }: {
      authorId?: null | string;
      mimetype?: string;
      name?: string;
      onProgress?: (progress: number) => void;
      uploadingKey?: string;
    } = {
      name: '',
      uploadingKey: '',
    },
  ) {
    try {
      if (uploadingKey) {
        runInAction(() => {
          this.uploading.set(uploadingKey, {
            error: null,
            file: null,
            inProgress: true,
            progress: 0,
            success: false,
          });

          onProgress?.(0);
        });
      }

      const uploadParamsResult = await getUploadParams(input, {
        authorId,
        mimetype,
        name,
      });

      const prepareResult = await apiRequest<CoreMediaFilesPrepareUploadParams>(
        {
          method: 'media.files.prepare_upload',
          params: {
            ...uploadParamsResult.params,
          },
        },
      );

      const uploadData = new FormData();

      Object.keys(prepareResult.fields).forEach(key => {
        uploadData.append(key, prepareResult.fields[key]);
      });

      uploadData.append('file', uploadParamsResult.file);

      const uploadResponse = await fetch(prepareResult.url, {
        body: uploadData,
        method: 'POST',
        // @ts-expect-error: TODO: describe why
        onProgress:
          process.env.NODE_ENV !== 'test'
            ? (progress: number) => {
                // leave 10% for the "read" request
                const currentProgress = Math.round(
                  progress > 90 ? 90 : progress,
                );

                this.updateUploadInfo(uploadingKey, {
                  progress: currentProgress,
                });

                onProgress?.(currentProgress);
              }
            : undefined,
        // Not documented option. To make it work patch "whatwg-fetch" package
        redirect: 'manual',
      });

      if (uploadResponse.type !== 'opaqueredirect' && !uploadResponse.ok) {
        const text = await uploadResponse.text();

        let message = text;

        // TODO: Log somewhere full error text
        const messageLookup = text.match(/<Message>([^<]+)<\/Message>/);

        if (messageLookup) {
          message = messageLookup[1]!;
        }

        throw new Error(`Upload file error: ${message}`);
      }

      // tell the backend the file has been uploaded
      await fetch(prepareResult.fields.success_action_redirect);

      // @ts-expect-error: TODO: describe why
      if (uploadParamsResult.file.close) {
        // @ts-expect-error: TODO: describe why
        uploadParamsResult.file.close();
      }

      const {file} = (await apiRequest<CoreMediaFilesReadParams>({
        method: 'media.files.read',
        params: {
          _id: prepareResult.file._id,
          author_id: authorId,
        },
      })) as {file: T};

      this.updateUploadInfo(uploadingKey, {
        file,
        inProgress: false,
        progress: 100,
        success: true,
      });

      onProgress?.(100);

      return file;
    } catch (error) {
      this.updateUploadInfo(uploadingKey, {
        error,
        inProgress: false,
        progress: 0,
        success: false,
      });

      onProgress?.(0);

      throw error;
    }
  }

  private updateUploadInfo(
    key: string | undefined,
    info: Partial<UploadInfo<T>>,
  ) {
    runInAction(() => {
      if (!key) {
        return;
      }

      const uploading = this.uploading.get(key);

      if (!uploading) {
        return;
      }

      Object.keys(info).forEach(infoKey => {
        uploading[infoKey] = info[infoKey];
      });
    });
  }
}

export const fileStore = new FileStore('file', {
  delete: 'media.files.delete',
  fetch: 'media.files.read',
});
